import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { Checkbox } from "@mui/material";
import OutlineIcon from "assets/icons/wizardFieldOutline.svg?react";
import FilledIcon from "assets/icons/wizardFieldFilled.svg?react";
import StandardIcon from "assets/icons/wizardFieldStandard.svg?react";
import ChevronIcon from "assets/icons/arrow-down.svg?react";
import FilesBg from "assets/icons/filesandfolder.svg?react";
import Input from "components/Input";
import { ContactFormFieldStyle } from "api/settingsService";
import styles from "../styles.module.scss";
import WizardActions from "../WizardActions";
import { useWizard } from "../WizardContext";

const ACCENT_COLORS = [
  "#5D49C7",
  "#639AFF",
  "#F75CCB",
  "#00502F",
  "#69A297",
  "#E9D758",
  "#002F5D",
  "#EAE2B7",
];

const THEMES = [
  { id: 0, c1: "#639AFF", c2: "#F75CCB" },
  { id: 1, c1: "#00502F", c2: "#A3C9A8" },
  { id: 2, c1: "#00502F", c2: "#69A297" },
  { id: 3, c1: "#00502F", c2: "#E9D758" },
  { id: 4, c1: "#002F5D", c2: "#C9DAEA" },
  { id: 5, c1: "#002F5D", c2: "#648DE5" },
];

const FIELD_STYLES: Array<{
  id: ContactFormFieldStyle;
  Icon: React.FC<React.SVGProps<SVGElement>>;
  labelKey: string;
  hintKey: string;
}> = [
  {
    id: "outlined",
    Icon: OutlineIcon,
    labelKey: "contactFormWizard.steps.formStyle.outline",
    hintKey: "contactFormWizard.steps.formStyle.outlineHint",
  },
  {
    id: "filled",
    Icon: FilledIcon,
    labelKey: "contactFormWizard.steps.formStyle.filled",
    hintKey: "contactFormWizard.steps.formStyle.filledHint",
  },
  {
    id: "standard",
    Icon: StandardIcon,
    labelKey: "contactFormWizard.steps.formStyle.standard",
    hintKey: "contactFormWizard.steps.formStyle.standardHint",
  },
];

interface CollapsibleSectionProps {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const CollapsibleSection = ({ title, open, onToggle, children }: CollapsibleSectionProps) => (
  <div className={styles.formStyleSection}>
    <button
      type="button"
      className={styles.formStyleSectionHdr}
      onClick={onToggle}
      aria-expanded={open}
    >
      <span>{title}</span>
      <ChevronIcon
        className={classNames(styles.formStyleChevron, open && styles.formStyleChevronOpen)}
      />
    </button>
    {open && (
      <div className={styles.formStyleSectionBody}>
        {children}
      </div>
    )}
  </div>
);

const Step5FormStyle = () => {
  const { t } = useTranslation();
  const { settings, patchSettings, uploadLogo, deleteLogo } = useWizard();

  const [openLogo, setOpenLogo] = useState(false);
  const [openHeader, setOpenHeader] = useState(false);
  const [openFieldStyle, setOpenFieldStyle] = useState(false);
  const [openAccent, setOpenAccent] = useState(false);
  const [openBg, setOpenBg] = useState(false);

  const onDrop = useCallback(
    (files: File[]) => { if (files[0]) uploadLogo(files[0]); },
    [uploadLogo],
  );

  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    accept: { "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"] },
    onDrop,
    multiple: false,
  });

  const logoUrl = useMemo(
    () => (settings.LogoBlob ? URL.createObjectURL(settings.LogoBlob) : null),
    [settings.LogoBlob],
  );

  return (
    <>
      <CollapsibleSection
        title={t("contactFormWizard.steps.branding.logoTitle")}
        open={openLogo}
        onToggle={() => setOpenLogo((v) => !v)}
      >
        <div
          {...getRootProps({
            className: classNames(
              styles.dropContainer,
              isDragAccept && styles.dropAccept,
              isDragReject && styles.dropReject,
            ),
          })}
        >
          <input {...getInputProps()} />
          {logoUrl ? (
            <>
              <img src={logoUrl} alt="Logo" className={styles.logoPreview} />
              <div className={styles.logoActions}>
                <div className={styles.buttonCTA}>{t("common.change")}</div>
                <div
                  className={styles.buttonCTASecondary}
                  onClick={(e) => { e.stopPropagation(); deleteLogo(); }}
                  role="button"
                >
                  {t("common.delete")}
                </div>
              </div>
            </>
          ) : (
            <>
              <FilesBg style={{ height: 60 }} />
              <div className={styles.buttonCTA}>{t("common.chooseFromDisk")}</div>
            </>
          )}
        </div>
        <div className={styles.logoHint}>
          {t("contactFormWizard.steps.branding.logoHint")}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title={t("contactFormWizard.steps.formStyle.headerTitle")}
        open={openHeader}
        onToggle={() => setOpenHeader((v) => !v)}
      >
        <div className={styles.headerTitleRow}>
          <div className={styles.headerTitleField}>
            <Input
              label={t("contactFormWizard.steps.formStyle.headerTitleLabel")}
              value={settings.HeaderTitle}
              onChange={(v: string) => patchSettings({ HeaderTitle: v.slice(0, 32) })}
              variant="filled"
              size="small"
              hideClearButton
              inputProps={{ maxLength: 32 }}
              RightIcon={
                <Checkbox
                  checked={settings.ShowHeader}
                  onChange={(e) => patchSettings({ ShowHeader: e.target.checked })}
                />
              }
            />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title={t("contactFormWizard.steps.formStyle.fieldStyleTitle")}
        open={openFieldStyle}
        onToggle={() => setOpenFieldStyle((v) => !v)}
      >
        <div className={styles.fieldStyleGrid}>
          {FIELD_STYLES.map(({ id, Icon, labelKey, hintKey }) => (
            <div
              key={id}
              className={classNames(
                styles.fieldStyleCard,
                settings.FieldStyle === id && styles.selected,
              )}
              onClick={() => patchSettings({ FieldStyle: id })}
              role="button"
            >
              <Icon />
              <div className={styles.styleLabel}>{t(labelKey)}</div>
              <div className={styles.styleHint}>{t(hintKey)}</div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title={t("contactFormWizard.steps.formStyle.accentTitle")}
        open={openAccent}
        onToggle={() => setOpenAccent((v) => !v)}
      >
        <div className={styles.colorSwatchGrid}>
          {ACCENT_COLORS.map((c) => (
            <div
              key={c}
              className={classNames(
                styles.colorSwatch,
                settings.AccentColor === c && styles.selected,
              )}
              onClick={() => patchSettings({ AccentColor: c })}
              role="button"
              aria-label={c}
            >
              <div className={styles.colorSwatchInner} style={{ background: c }} />
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title={t("contactFormWizard.steps.branding.bgTitle")}
        open={openBg}
        onToggle={() => setOpenBg((v) => !v)}
      >
        <div className={styles.themeGrid}>
          {THEMES.map((theme) => (
            <div
              key={theme.id}
              className={classNames(
                styles.themeSwatch,
                settings.Theme === theme.id && styles.selected,
              )}
              onClick={() => patchSettings({ Theme: theme.id })}
              role="button"
              aria-label={`theme ${theme.id + 1}`}
            >
              <div className={styles.themeSwatchInner}>
                <span className={styles.themeHalf} style={{ background: theme.c1 }} />
                <span className={styles.themeHalf} style={{ background: theme.c2 }} />
              </div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <WizardActions autonomous />
    </>
  );
};

export default Step5FormStyle;
